import { zodResolver } from '@hookform/resolvers/zod';
import {
  type PaymentMethodDtoWithIsPreferred,
  customerControllerCharge,
  customerControllerGetCustomerPaymentMethodsQueryOptions,
} from 'korint-sdk';
import { type ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form } from '../../components/forms/form/form';
import { PaymentMethodStatusBadge } from '../../components/features/payment';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { FormSelectInput } from '../../components/forms/form-select-input';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type RetryChargeOnInvoiceProps = {
  customerId: string;
  invoiceId: string;
  trigger: ReactNode;
  handleSuccess?(): Promise<void> | void;
};

const label = (paymentMethod: PaymentMethodDtoWithIsPreferred) => {
  if (paymentMethod.paymentMethodType === 'sepa_debit') {
    return `IBAN terminant par ${paymentMethod?.last4}`;
  }

  const base = `Carte bancaire terminant par ${paymentMethod?.last4}`;

  if (paymentMethod.expirationYear && paymentMethod.expirationMonth) {
    return `${base} (${new Date(
      paymentMethod.expirationYear,
      paymentMethod.expirationMonth - 1,
    ).toLocaleDateString('fr-FR', { month: '2-digit', year: 'numeric' })})`;
  }
  return base;
};

const isUsable = (paymentMethod: PaymentMethodDtoWithIsPreferred) =>
  paymentMethod.status === 'ACTIVE' && !paymentMethod.deletedAt;

export const RetryChargeOnInvoice = ({
  customerId,
  invoiceId,
  trigger,
  handleSuccess,
}: RetryChargeOnInvoiceProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { data: paymentMethods } = useQuery(
    customerControllerGetCustomerPaymentMethodsQueryOptions(customerId),
  );

  const usablePaymentMethods = useMemo(
    () => (paymentMethods?.data ?? []).filter(isUsable),
    [paymentMethods],
  );

  const paymentMethodOptions = useMemo(
    () =>
      (paymentMethods?.data ?? []).map(paymentMethod => ({
        value: paymentMethod.id,
        label: (
          <span className="flex items-center gap-2">
            {label(paymentMethod)}
            <PaymentMethodStatusBadge status={paymentMethod.status} />
          </span>
        ),
      })),
    [paymentMethods],
  );

  const schema = z.object({
    paymentMethodId: z.enum(
      (paymentMethods?.data ?? []).map(({ id }) => id) as [string, ...string[]],
      {
        required_error: t('invoices.module.paymentMethodRequired'),
      },
    ),
  });

  type PaymentMethodsFormValues = z.infer<typeof schema>;

  const form = useForm<PaymentMethodsFormValues>({
    resolver: zodResolver(schema),
  });

  const selectedPaymentMethodId = form.watch('paymentMethodId');
  const selectedPaymentMethod = (paymentMethods?.data ?? []).find(
    ({ id }) => id === selectedPaymentMethodId,
  );
  const selectedIsInvalid = !!selectedPaymentMethod && !isUsable(selectedPaymentMethod);

  const { mutateAsync: onSubmit } = useMutation({
    mutationFn: (v: PaymentMethodsFormValues) =>
      customerControllerCharge(customerId, {
        invoiceId,
        paymentMethodId: v.paymentMethodId,
      }),
    onSuccess: () => {
      handleSuccess?.();
    },
    onSettled: () => {
      setOpen(false);
    },
  });

  return (
    <Form {...form}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent>
          <DialogTitle>
            {usablePaymentMethods.length === 0
              ? t('invoices.module.retryPaymentBlockedTitle')
              : t('invoices.module.retryPaymentTitle')}
          </DialogTitle>
          <Stack asChild>
            <form
              id="retry-charge-form"
              onSubmit={form.handleSubmit(formValues => onSubmit(formValues))}
            >
              <FormSelectInput
                label={t('invoices.module.paymentMethodLabel')}
                placeholder={t('invoices.module.paymentMethodPlaceholder')}
                name="paymentMethodId"
                required
                control={form.control}
                options={paymentMethodOptions}
              />
              {usablePaymentMethods.length === 0 ? (
                <Text size="sm" tone="destructive">
                  {t('invoices.module.noValidPaymentMethod')}
                </Text>
              ) : (
                selectedIsInvalid && (
                  <Text size="sm" tone="destructive">
                    {t('invoices.module.invalidPaymentMethodSelected')}
                  </Text>
                )
              )}
              <DialogFooter>
                <Button
                  type="submit"
                  loading={form.formState.isSubmitting}
                  disabled={usablePaymentMethods.length === 0 || selectedIsInvalid}
                  form="retry-charge-form"
                >
                  {t('invoices.module.pay')}
                </Button>
              </DialogFooter>
            </form>
          </Stack>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
